import { action, observable } from "mobx";

export const rootStore = observable({
  me: null,
  user_info: null,
  notification: null,
  signup_info: null,
  class_id: null,
  is_corp_mode: false,
  corp_info: null,
  permissions: [], // 사용자 권한 배열

  //setting_modal. 설정모달에서 확인버튼 표시용으로 쓰일 서브모달
  settingSubModal: null,
  payment_info: {
    method: null, // "CARD" | "NAVER_PAY" | "KAKAO_PAY" | null
    cardCompany: "", // 카드사 선택값
    installment: "", // 할부 개월 수
    signature: null, // { data: string, width: number, height: number } | null
  },

  /* **********************************
   * 모달
   ********************************** */
  openSettingSubModal: action(function (content) {
    rootStore.settingSubModal = content;
  }),

  closeSettingSubModal: action(function () {
    rootStore.settingSubModal = null;
  }),

  /* **********************************
   * 결제 정보
   ********************************** */
  setPaymentInfo: action(function (info) {
    rootStore.payment_info = {
      ...rootStore.payment_info,
      ...info,
    };
    sessionStorage.setItem(
      "payment_info",
      JSON.stringify(rootStore.payment_info)
    );
  }),

  clearPaymentInfo: action(function () {
    rootStore.payment_info = {
      method: null,
      cardCompany: "",
      installment: "",
      signature: null,
    };
    sessionStorage.removeItem("payment_info");
  }),

  /* **********************************
   * 로그인
   ********************************** */
  signIn: action((user, platform) => {
    rootStore.me = {
      ...user,
      platform: platform,
    };
    localStorage.setItem(
      "admin",
      JSON.stringify({
        ...user,
        platform: platform,
      })
    );

    // 로그인 성공 후 최근 로그인 플랫폼 저장
    localStorage.setItem("recent_login_platform", platform);
  }),

  signOut: action(() => {
    // MobX store 초기화
    rootStore.me = null;
    rootStore.user_info = null;
    rootStore.class_id = null;
    rootStore.signup_info = null;
    rootStore.is_corp_mode = false;
    rootStore.corp_info = null;
    rootStore.permissions = [];
    rootStore._token = ""; // Clear token from store

    // 모든 인증 관련 데이터 삭제
    localStorage.removeItem("admin");
    localStorage.removeItem("user_info");
    localStorage.removeItem("refresh_token");
    localStorage.removeItem("signup_info");
    localStorage.removeItem("payment_info");
    localStorage.removeItem("permissions");
    localStorage.removeItem("corp_info");

    // 세션 스토리지도 정리
    sessionStorage.clear();

    // 쿼리 캐시 정리 (React Query 캐시)
    if (typeof window !== "undefined" && window.queryClient) {
      window.queryClient.clear();
    }

    // 보존할 항목들은 그대로 유지
    // - recent_login_platform
    // - saved_email
  }),

  /* **********************************
   * Token
   ********************************** */
  _token: "", // 내부 토큰 저장용

  // 앱 초기화 시 토큰 복원
  initializeToken: action(() => {
    if (!rootStore._token) {
      const me = rootStore.getMe;
      if (me?.accessToken) {
        rootStore._token = me.accessToken;
      }
    }
  }),

  setToken: action((accessToken) => {
    rootStore._token = accessToken;

    // localStorage의 admin 정보도 업데이트
    const me = rootStore.getMe;
    if (me) {
      me.accessToken = accessToken;
      rootStore.me = me;
      localStorage.setItem("admin", JSON.stringify(me));
    } else if (accessToken) {
      // me가 없지만 토큰이 있는 경우, 최소한의 정보로 저장
      const adminData = { accessToken };
      rootStore.me = adminData;
      localStorage.setItem("admin", JSON.stringify(adminData));
    }
  }),

  /* **********************************
   * 프로필
   ********************************** */
  setProfile: action((user_info) => {
    rootStore.user_info = user_info;

    if (user_info) {
      try {
        localStorage.setItem("user_info", JSON.stringify(user_info));

        if (user_info.userId) {
          localStorage.setItem("saved_email", user_info.userId);
        }

        // permissions도 함께 저장
        if (user_info.permissions) {
          rootStore.setPermissions(user_info.permissions);
        }
      } catch (error) {
        console.error("Error saving user_info to localStorage:", error);
      }
    } else {
      localStorage.removeItem("user_info");
      rootStore.clearPermissions();
    }
  }),

  /* **********************************
   * 권한 관리
   ********************************** */
  setPermissions: action((permissions) => {
    rootStore.permissions = permissions;
    localStorage.setItem("permissions", JSON.stringify(permissions));
  }),

  clearPermissions: action(() => {
    rootStore.permissions = [];
    localStorage.removeItem("permissions");
  }),

  // 기관 정보 저장
  setCorpInfo: action(function (corpInfo) {
    rootStore.corp_info = corpInfo;
    localStorage.setItem("corp_info", JSON.stringify(corpInfo));
  }),

  /* **********************************
   * 클래스 ID
   ********************************** */
  setClassId: action((class_id) => {
    rootStore.class_id = class_id;
    sessionStorage.setItem("class_id", JSON.stringify(class_id));
  }),

  /* **********************************
   * 회원가입 정보
   ********************************** */
  setSignUpInfo: action((signup_info) => {
    rootStore.signup_info = signup_info;
    localStorage.setItem("signup_info", JSON.stringify(signup_info));
  }),

  /* **********************************
   * 회사 모드
   ********************************** */
  setCorpMode: action((corp_mode) => {
    rootStore.is_corp_mode = corp_mode;
    sessionStorage.setItem("is_corp_mode", JSON.stringify(corp_mode));
  }),

  /* **********************************
   * 로그인 정보
   ********************************** */
  get getMe() {
    if (rootStore.me == null) {
      try {
        rootStore.me = JSON.parse(localStorage.getItem("admin"));
      } catch (error) {
        console.log(error);
      }
    }
    return rootStore.me;
  },

  /* **********************************
   * 프로필
   ********************************** */
  get getProfile() {
    if (rootStore.user_info == null) {
      try {
        const userInfoStr = localStorage.getItem("user_info");

        if (userInfoStr) {
          rootStore.user_info = JSON.parse(userInfoStr);
        } else {
          rootStore.user_info = null;
        }
      } catch (error) {
        console.error("Error parsing user_info:", error);
        rootStore.user_info = null;
      }
    }

    return rootStore.user_info;
  },

  /* **********************************
   * 권한
   ********************************** */
  get getPermissions() {
    if (rootStore.permissions.length === 0) {
      try {
        const permissionsStr = localStorage.getItem("permissions");
        if (permissionsStr) {
          rootStore.permissions = JSON.parse(permissionsStr);
        }
      } catch (error) {
        console.error("Error parsing permissions:", error);
        rootStore.permissions = [];
      }
    }
    return rootStore.permissions;
  },

  /* **********************************
   * 로그인 여부
   ********************************** */
  get isSignedIn() {
    // 토큰이 있는지만 확인 (사용자 정보는 나중에 로드될 수 있음)
    const hasToken = rootStore.token && rootStore.token.length > 0;

    // 토큰이 있으면 로그인된 것으로 간주
    return hasToken;
  },

  /* **********************************
   * 토큰
   ********************************** */
  get token() {
    // rootStore._token이 비어있으면 localStorage에서 복원
    if (!rootStore._token) {
      const me = rootStore.getMe;
      if (me?.accessToken) {
        rootStore._token = me.accessToken;
      } else {
        // localStorage에서 직접 토큰 확인
        const adminData = localStorage.getItem("admin");
        if (adminData) {
          try {
            const parsed = JSON.parse(adminData);
            if (parsed.accessToken) {
              rootStore._token = parsed.accessToken;
            }
          } catch (error) {
            console.log(error);
          }
        }
      }
    }
    return rootStore._token;
  },

  /* **********************************
   * 클래스 ID
   ********************************** */
  get getClassId() {
    if (rootStore.class_id == null) {
      try {
        rootStore.class_id = JSON.parse(sessionStorage.getItem("class_id"));
      } catch (error) {
        console.log(error);
      }
    }
    return rootStore.class_id;
  },

  /* **********************************
   * 회원가입 정보
   ********************************** */
  get getSignUpInfo() {
    if (rootStore.signup_info == null) {
      try {
        rootStore.signup_info = JSON.parse(localStorage.getItem("signup_info"));
      } catch (error) {
        console.log(error);
      }
    }
    return rootStore.signup_info;
  },

  /* **********************************
   * 회사 모드
   ********************************** */
  get getCorpMode() {
    if (sessionStorage.getItem("is_corp_mode") == null) {
      const currentPath = window.location.pathname;
      const initialMode = currentPath.startsWith("/corp");
      sessionStorage.setItem("is_corp_mode", JSON.stringify(initialMode));
      rootStore.is_corp_mode = initialMode;
    } else {
      rootStore.is_corp_mode = JSON.parse(
        sessionStorage.getItem("is_corp_mode")
      );
    }
    return rootStore.is_corp_mode;
  },

  get getCorpInfo() {
    if (rootStore.corp_info == null) {
      rootStore.corp_info = JSON.parse(localStorage.getItem("corp_info"));
    }
    return rootStore.corp_info;
  },

  /* **********************************
   * 결제 정보
   ********************************** */
  get getPaymentInfo() {
    if (rootStore.payment_info == null) {
      try {
        const saved = sessionStorage.getItem("payment_info");
        if (saved) {
          rootStore.payment_info = JSON.parse(saved);
        } else {
          rootStore.payment_info = {
            method: null,
            cardCompany: "",
            installment: "",
            signature: null,
          };
        }
      } catch (error) {
        console.log(error);
      }
    }
    return rootStore.payment_info;
  },
});
